import React, { useState, useRef, useMemo, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import { process } from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import {
  Search,
  LayoutGrid,
  List,
  User,
  Calendar,
  Receipt,
  ChevronDown,
  ChevronUp,
  X,
} from "lucide-react";
//import { ColumnMenu } from '../../components/ColumnMenu';
import { ColumnMenuClaim } from "../../components/ColumnMenuClaim";
import { CardsPagination } from "../../components/CardsPagination";
import { claims, members } from "../../data/mockData";
import { claimApi } from "../../api/claimApi";
import { isApiConfigured } from "../../api/axiosClient";
import { LoadingSpinner, ErrorCard } from "../../components/LoadingSpinner";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../components/Toast";

import { formatDate } from "../../components/utils/formatDate";
import { Loader } from "@progress/kendo-react-indicators";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { userApi } from "../../api/userApi";

// In-memory cache of the last claim search (criteria + results). Survives
// client-side navigation (e.g. list → claim detail → back) so the user doesn't
// re-search, but is NOT written to storage — claim data/PHI never touches disk,
// and it clears on a full page reload or tab close.
let claimSearchCache = null;

const ClaimStatusPage = () => {
  const toast = useToast();
  const location = useLocation();
  const [claimDetails, setClaimDetails] = useState({});
  // const [claimDetails, setClaimDetails] = useState([]);
  const [detailLoading, setDetailLoading] = useState({});
  const [claimDetailsLoading, setClaimDetailsLoading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [searchBy, setSearchBy] = useState("0");
  // The single-field value used when Search By is Member ID / Claim # / Account # / Check #
  const [searchValue, setSearchValue] = useState("");
  // The 3-field combo used when Search By is "First Name, Last Name & DOB"
  const [searchFirstName, setSearchFirstName] = useState("");
  const [searchLastName, setSearchLastName] = useState("");
  const [searchDob, setSearchDob] = useState("");
  const [billingProviderId, setBillingProviderId] = useState("");
  const [groupId, setGroupId] = useState("");
  const today = new Date().toISOString().split("T")[0];
  const [serviceEnd, setServiceEnd] = useState(today);
  const [serviceStart, setServiceStart] = useState("2010-01-01");
  //const [serviceStart, setServiceStart] = useState(new Date('2025-01-01').toLocaleDateString("en-US"));
  const [searched, setSearched] = useState(false);
  const searchTimeoutRef = useRef(null);
  //const [results, setResults] = useState([]);
  const [results, setResults] = useState([]);
  const [totalSummary, setTotalSummary] = useState({});
  const [isItTextSearch, setIsItTextSearch] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [dataState, setDataState] = useState({
    skip: 0,
    take: 10,
    sort: [],
    filter: null,
  });

  //const [expandedItems, setExpandedItems] = useState();
  const [expandedItems, setExpandedItems] = useState([]);
  // 'cards' | 'table' — matches the User Management view toggle
  const [viewMode, setViewMode] = useState("cards");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Free-text search across all columns — works in Cards and Table.
  const [searchText, setSearchText] = useState("");
  const excelExportRef = useRef(null);
  const [IsExportingToExcel, setIsExportingToExcel] = useState(false);

  const [userTaxIds, setUserTaxIds] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);
  const [cardDetailLoading, setCardDetailLoading] = useState(false);

  const savedState = location.state;
  const isRestoring = useRef(false);

  const [selectedUserId, setSelectedUserId] = useState(
    savedState?.selectedUserId,
  );

  //console.log("SAVEDSTATE : "+JSON.stringify(savedState));

  useEffect(() => {
    fetchGroups();
  }, []);

  // Restore the previous search (criteria + results) on mount so returning from
  // a claim detail page shows the same results without searching again.
  useEffect(() => {
    const s = claimSearchCache;
    if (!s) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (s.searchBy != null) setSearchBy(s.searchBy);
    if (s.searchValue != null) setSearchValue(s.searchValue);
    if (s.searchFirstName != null) setSearchFirstName(s.searchFirstName);
    if (s.searchLastName != null) setSearchLastName(s.searchLastName);
    if (s.searchDob != null) setSearchDob(s.searchDob);
    if (s.billingProviderId != null) setBillingProviderId(s.billingProviderId);
    if (s.groupId != null) setGroupId(s.groupId);
    if (s.serviceStart != null) setServiceStart(s.serviceStart);
    if (s.serviceEnd != null) setServiceEnd(s.serviceEnd);
    if (Array.isArray(s.results)) setResults(s.results);
    if (s.totalRecords != null) setTotalRecords(s.totalRecords);
    if (s.dataState) setDataState(s.dataState);
    if (s.viewMode) setViewMode(s.viewMode);
    if (s.searched) setSearched(true);
    if (s.totalSummary) setTotalSummary(s.totalSummary);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist the search after results/paging/view change so it survives
  // navigation to a claim detail and back (in-memory; see claimSearchCache).
  useEffect(() => {
    if (!searched) return;
    claimSearchCache = {
      searchBy,
      searchValue,
      searchFirstName,
      searchLastName,
      searchDob,
      billingProviderId,
      groupId,
      serviceStart,
      serviceEnd,
      searched,
      results,
      totalRecords,
      dataState,
      viewMode,
      totalSummary,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searched, results, totalRecords, dataState, viewMode]);

  useEffect(() => {
    // alert("selectedUserId : " + selectedUserId);

    if (selectedUserId) {
      //  alert("savedState?.selectedUserId : " + savedState?.selectedUserId);
      getUserTaxIds(selectedUserId);
    } else {
      if (user?.userId) {
        getUserTaxIds(user?.userId);
      }
    }
  }, [user?.userId, selectedUserId]);

  //useEffect(() => {
  //    if (user?.userId) {
  //        getUserTaxIds(user?.userId);
  //    }
  //}, [user?.userId]);

  const getUserTaxIds = async (userId) => {
    if (isApiConfigured()) {
      setPageLoading(true);

      try {
        var res = await userApi.getUsersTaxIds({
          userId: userId,
        });

        if (res.success) {
          setUserTaxIds(res?.data?.TaxIds);
          console.log("UserTaxIds get successfully");
        } else {
          setUserTaxIds([]);
          console.log(res.message);
        }

        setPageLoading(false);
      } catch (err) {
        setPageLoading(false);
        console.log("Error at getUserTaxIds : " + err);
      } finally {
      }
    }
  };

  const fetchGroups = async () => {
    const res = await userApi.groups();

    if (res.success) {
      setGroups(res.data.Groups);
    }
  };

  const parseAmount = (value) => {
    if (!value) return 0;

    const isNegative = value.includes("(") && value.includes(")");

    const amount = parseFloat(value.replace(/[$,()]/g, ""));

    return isNegative ? -amount : amount;
  };

  const parseDate = (str) => {
    if (!str) return null;

    const [month, day, year] = str.split("/");
    return new Date(year, month - 1, day);
  };

  useEffect(() => {
    //  console.log("location.state?.fromDetails : " + location.state?.fromDetails);
    if (!location.state?.fromDetails) return;
    if (!savedState) return;

    isRestoring.current = true;

    setSearchBy(savedState.searchBy || "0");
    setSearchValue(savedState.searchValue || "");
    setSearchFirstName(savedState.searchFirstName || "");
    setSearchLastName(savedState.searchLastName || "");
    setSearchDob(savedState.searchDob || "");
    setBillingProviderId(savedState.billingProviderId || "");
    setGroupId(savedState.groupId || "");
    setServiceStart(savedState.serviceStart || "");
    setServiceEnd(savedState.serviceEnd || "");
    setSearched(savedState.searched || false);

    setResults(savedState.results || []);
    setTotalRecords(savedState.totalRecords || []);
    setSearchText(savedState.searchText || "");
    setViewMode(savedState.viewMode || "cards");
    setExpandedItems(savedState.expandedItems || []);
    //setExpandedItems(
    //    savedState?.expandedItems
    //        ? savedState.expandedItems
    //        : []
    //);

    //   setExpandedItems([savedState.expandedItems]);

    //setClaimDetails({
    //    [savedState?.claimDetails?.claimNumber]: savedState?.claimDetails
    //});

    //console.log("----------------------------------------------------------");
    //console.log("savedState?.claimDetails : " + savedState?.claimDetails);
    //console.log("----------------------------------------------------------");

    setClaimDetails(
      savedState?.claimDetails
        ? {
            [savedState?.claimDetails?.claimNumber]: savedState?.claimDetails,
          }
        : {},
    );

    setSelectedUserId(savedState?.selectedUserId);

    if (!savedState?.claimDetails) {
      setExpandedItems([]);
    }

    // Restore grid/card exact page + sorting
    setDataState(
      savedState.dataState || {
        skip: 0,
        take: 10,
        sort: [],
        filter: null,
      },
    );

    setTimeout(() => {
      isRestoring.current = false;
    }, 0);

    navigate(location.pathname, {
      replace: true,
      state: null,
    });
  }, [savedState]);

  const handleSearch = async (
    customState = dataState,
    customSearch = searchText,
  ) => {
    setError(null);
    setSearched(true);
    setIsItTextSearch(false);
    setDataState({ skip: 0, take: 10, sort: [], filter: null });
    setExpandedItems([]);
    setSearchText("");

    if (!billingProviderId?.trim()) {
      toast.error("Billing Provider Id is required.", {
        title: "Missing information",
      });
      return;
    }

    // Build the Search-By payload (what the user typed in the dynamic input)
    const searchByParams = { searchBy };

    const fieldLabels = {
      memberId: "Member ID",
      claimNumber: "Claim Number",
      accountNumber: "Account Number",
      checkNumber: "Check Number",
    };

    if (
      ["memberId", "claimNumber", "accountNumber", "checkNumber"].includes(
        searchBy,
      ) &&
      !searchValue
    ) {
      toast.error(`${fieldLabels[searchBy]} is required.`, {
        title: "Missing information",
      });
      return;
    }

    if (
      ["memberId", "claimNumber", "accountNumber", "checkNumber"].includes(
        searchBy,
      ) &&
      searchValue
    ) {
      searchByParams.searchValue = searchValue.trim();
    } else if (searchBy === "firstLastDob") {
      if (searchFirstName === "" || searchLastName === "" || searchDob === "") {
        toast.error("First Name, Last Name, and DOB are required.", {
          title: "Missing information",
        });
        return;
      }

      if (searchFirstName) searchByParams.firstName = searchFirstName.trim();
      if (searchLastName) searchByParams.lastName = searchLastName.trim();
      if (searchDob) searchByParams.dob = searchDob;
    }

    if (isApiConfigured()) {
      setLoading(true);
      const sort = customState.sort?.[0];
      try {
        console.log("--------------------------------------------");
        console.log("searchByParams:", searchByParams);
        console.log("billingProviderId:", billingProviderId);
        console.log("group:", groupId);
        console.log("serviceStart:", serviceStart);
        console.log("serviceEnd:", serviceEnd);
        console.log("user:", user);
        console.log("searchText:", customSearch || "");
        console.log("sortColumnName:", sort?.field || "");
        console.log("sortColumnDirection:", sort?.dir || "");
        console.log("startIndex:", customState.skip || 0);
        console.log("pageSize:", customState.take || 10);
        console.log("--------------------------------------------");

        const response = await claimApi.searchClaims({
          searchByParams,
          billingProviderId,
          group: groupId,
          serviceStart,
          serviceEnd,
          //  dataState,
          user,
          //  searchText: customSearch || "",
          //  sortColumnName: sort?.field || "",
          //   sortColumnDirection: sort?.dir || "",
          startIndex: customState.skip || 0,
          pageSize: customState.take || 10,
        });

        // alert(JSON.stringify(response.data.Claims));

        const formattedData = (response.data.Claims || []).map((item) => ({
          ...item,
          //Service_Date: new Date(item?.Service_Date?.split(' ')[0]),
          Service_Date: parseDate(item?.Service_Date?.split(" ")[0]),
          //Check_Date: new Date(item?.Check_Date?.split(' ')[0]),
          Check_Date: parseDate(item?.Check_Date?.split(" ")[0]),
          Received_Date: parseDate(item?.Received_Date?.split(" ")[0]),
          Billed_Amount: parseAmount(item?.Billed_Amount),
          Paid_Amount: parseAmount(item?.Paid_Amount),
          Mem_Rep: parseAmount(item?.Mem_Rep),
          Member_Id: Number(item?.Member_Id?.replace("-", "")),
        }));

        //  alert("FORMATTED DATA : " + JSON.stringify(formattedData));

        setResults(formattedData || []);

        //alert(JSON.stringify(response?.data.Summary));

        //console.log("CLAIM COUNT RESPONSE : "+JSON.stringify(response?.data.Summary));
        setTotalSummary(response?.data.Summary || {});
        setTotalRecords(response?.data.Claims[0]?.TotalRecord || 0);
      } catch (err) {
        console.log("------------------------------------");
        console.log("HANDLE SEARCH ERROR : " + err);
        console.log("------------------------------------");
        setError(
          err.response?.data?.message ||
            "Failed to search claims. Please try again.",
        );
        setResults([]);
        setTotalRecords(0);
      } finally {
        setLoading(false);
        setError(null);
      }
    } else {
      // Mock data fallback — flip `loading` briefly so the Search button
      // still shows "Searching…" + disables, matching the real API UX.
      setLoading(true);
      await new Promise((r) => setTimeout(r, 350));
      let filtered = claims.filter(
        (c) => c.billingProviderId === billingProviderId,
      );

      if (groupId) {
        filtered = filtered.filter(
          (c) => !c.groupNumber || c.groupNumber === groupId,
        );
      }
      if (serviceStart) {
        filtered = filtered.filter((c) => c.serviceDate >= serviceStart);
      }
      if (serviceEnd) {
        filtered = filtered.filter((c) => c.serviceDate <= serviceEnd);
      }

      // Apply "Search By" filter against the right field
      const v = (searchValue || "").trim().toLowerCase();
      if (searchBy === "memberId" && v) {
        filtered = filtered.filter((c) =>
          (c.memberId || "").toLowerCase().includes(v),
        );
      } else if (searchBy === "claimNumber" && v) {
        filtered = filtered.filter((c) =>
          (c.claimNumber || "").toLowerCase().includes(v),
        );
      } else if (searchBy === "accountNumber" && v) {
        filtered = filtered.filter((c) =>
          (c.patientAccountNo || "").toLowerCase().includes(v),
        );
      } else if (searchBy === "checkNumber" && v) {
        filtered = filtered.filter((c) =>
          (c.checkNumber || "").toLowerCase().includes(v),
        );
      } else if (searchBy === "firstLastDob") {
        const qFirst = searchFirstName.trim().toLowerCase();
        const qLast = searchLastName.trim().toLowerCase();
        if (qFirst || qLast || searchDob) {
          // Cross-reference the members mock to find matches, then filter
          // claims by the resulting memberIds.
          const matchedIds = members
            .filter(
              (m) =>
                (!qFirst || m.firstName.toLowerCase().includes(qFirst)) &&
                (!qLast || m.lastName.toLowerCase().includes(qLast)) &&
                (!searchDob || m.dateOfBirth === searchDob),
            )
            .map((m) => m.memberId);
          filtered = filtered.filter((c) => matchedIds.includes(c.memberId));
        }
      }

      setResults(filtered);
      setLoading(false);
    }
  };
  const formatSearchText = (text) => {
    const regex = /^\d{2}\/\d{2}\/\d{4}$/;

    if (!regex.test(text)) {
      return text;
    }

    const [month, day, year] = text.split("/");
    return `${year}-${month}-${day}`;
  };

  const exportToExcel = async (
    customState = dataState,
    customSearch = searchText,
  ) => {
    if (!billingProviderId?.trim()) {
      toast.error("Billing Provider Id is required.", {
        title: "Missing information",
      });
      return;
    }

    // Build the Search-By payload (what the user typed in the dynamic input)
    const searchByParams = { searchBy };

    const fieldLabels = {
      memberId: "Member ID",
      claimNumber: "Claim Number",
      accountNumber: "Account Number",
      checkNumber: "Check Number",
    };

    if (
      ["memberId", "claimNumber", "accountNumber", "checkNumber"].includes(
        searchBy,
      ) &&
      !searchValue
    ) {
      toast.error(`${fieldLabels[searchBy]} is required.`, {
        title: "Missing information",
      });
      return;
    }

    if (
      ["memberId", "claimNumber", "accountNumber", "checkNumber"].includes(
        searchBy,
      ) &&
      searchValue
    ) {
      searchByParams.searchValue = searchValue.trim();
    } else if (searchBy === "firstLastDob") {
      if (searchFirstName === "" || searchLastName === "" || searchDob === "") {
        toast.error("First Name, Last Name, and DOB are required.", {
          title: "Missing information",
        });
        return;
      }

      if (searchFirstName) searchByParams.firstName = searchFirstName.trim();
      if (searchLastName) searchByParams.lastName = searchLastName.trim();
      if (searchDob) searchByParams.dob = searchDob;
    }

    //const searchByParams = { searchBy };
    const sort = customState.sort?.[0];

    console.log("---------------EXPORT TO EXCEL-------------------");
    console.log("searchByParams:", searchByParams);
    console.log("billingProviderId:", billingProviderId);
    console.log("group:", groupId);
    console.log("serviceStart:", serviceStart);
    console.log("serviceEnd:", serviceEnd);
    console.log("user:", user);
    console.log("searchText:", customSearch || "");
    console.log("sortColumnName:", sort?.field || "");
    console.log("sortColumnDirection:", sort?.dir || "");
    console.log("startIndex:", customState.skip || 0);
    console.log("pageSize:", customState.take || 10);
    console.log("--------------------------------------------");

    if (isApiConfigured()) {
      try {
        setIsExportingToExcel(true);

        const response = await claimApi.searchClaims({
          searchByParams,
          billingProviderId,
          group: groupId,
          serviceStart,
          serviceEnd,
          user,
          searchText: formatSearchText(searchText || ""),
          sortColumnName: sort?.field || "",
          sortColumnDirection: sort?.dir || "",
          //startIndex: customState.skip || 0,
          startIndex: 0,
          pageSize: totalRecords,
        });

        const formattedData = (response.data.Claims || []).map((item) => ({
          ...item,
          //Service_Date: new Date(item?.Service_Date?.split(' ')[0]),
          Service_Date: parseDate(item?.Service_Date?.split(" ")[0]),
          //Check_Date: new Date(item?.Check_Date?.split(' ')[0]),
          Check_Date: parseDate(item?.Check_Date?.split(" ")[0]),
          Received_Date: parseDate(item?.Received_Date?.split(" ")[0]),
          Billed_Amount: parseAmount(item?.Billed_Amount),
          Paid_Amount: parseAmount(item?.Paid_Amount),
          Mem_Rep: parseAmount(item?.Mem_Rep),
          Member_Id: Number(item?.Member_Id?.replace("-", "")),
        }));

        //console.log("------------------------------------------------");
        //console.log("EXPORT TO EXCEL DATA  : " + formattedData);
        //console.log("TOTAL DATA GET : " + formattedData.length);
        //console.log("------------------------------------------------");

        setIsExportingToExcel(false);

        return formattedData;
      } catch (err) {
        setIsExportingToExcel(false);
        console.log("-------------------------------------");
        console.log("exportToExcel ERROR : " + err);
        console.log("-------------------------------------");
        setError(err.response?.data?.message || "Failed to get claims.");
      } finally {
        setIsExportingToExcel(false);
      }
    } else {
      toast.error("API not configured.", { title: "Search Claim" });
    }
  };

  const handleSearch2 = async (
    customState = dataState,
    customSearch = searchText,
  ) => {
    //setExpandedItems([]);
    setError(null);
    setSearched(true);
    setLoading(false);

    setIsItTextSearch(true);

    if (!billingProviderId?.trim()) {
      toast.error("Billing Provider Id is required.", {
        title: "Missing information",
      });
      return;
    }

    // Build the Search-By payload (what the user typed in the dynamic input)
    const searchByParams = { searchBy };

    const fieldLabels = {
      memberId: "Member ID",
      claimNumber: "Claim Number",
      accountNumber: "Account Number",
      checkNumber: "Check Number",
    };

    if (
      ["memberId", "claimNumber", "accountNumber", "checkNumber"].includes(
        searchBy,
      ) &&
      !searchValue
    ) {
      toast.error(`${fieldLabels[searchBy]} is required.`, {
        title: "Missing information",
      });
      return;
    }

    if (
      ["memberId", "claimNumber", "accountNumber", "checkNumber"].includes(
        searchBy,
      ) &&
      searchValue
    ) {
      searchByParams.searchValue = searchValue.trim();
    } else if (searchBy === "firstLastDob") {
      if (searchFirstName === "" || searchLastName === "" || searchDob === "") {
        toast.error("First Name, Last Name, and DOB are required.", {
          title: "Missing information",
        });
        return;
      }

      if (searchFirstName) searchByParams.firstName = searchFirstName.trim();
      if (searchLastName) searchByParams.lastName = searchLastName.trim();
      if (searchDob) searchByParams.dob = searchDob;
    }

    if (isApiConfigured()) {
      setGridLoading(true);

      const sort = customState.sort?.[0];
      try {
        //console.log("--------------------------------------------");
        //console.log("searchByParams:", searchByParams);
        //console.log("billingProviderId:", billingProviderId);
        //console.log("group:", groupId);
        //console.log("serviceStart:", serviceStart);
        //console.log("serviceEnd:", serviceEnd);
        //console.log("user:", user);
        //console.log("searchText:", customSearch || "");
        //console.log("sortColumnName:", sort?.field || "");
        //console.log("sortColumnDirection:", sort?.dir || "");
        //console.log("startIndex:", customState.skip || 0);
        //console.log("pageSize:", customState.take || 10);
        //console.log("--------------------------------------------");

        const response = await claimApi.searchClaims({
          searchByParams,
          billingProviderId,
          group: groupId,
          serviceStart,
          serviceEnd,
          //  dataState,
          user,
          searchText: formatSearchText(customSearch || ""),
          sortColumnName: sort?.field || "",
          sortColumnDirection: sort?.dir || "",
          startIndex: customState.skip || 0,
          pageSize: customState.take || 10,
        });

        const formattedData = (response.data.Claims || []).map((item) => ({
          ...item,
          //Service_Date: new Date(item?.Service_Date?.split(' ')[0]),
          Service_Date: parseDate(item?.Service_Date?.split(" ")[0]),
          //Check_Date: new Date(item?.Check_Date?.split(' ')[0]),
          Check_Date: parseDate(item?.Check_Date?.split(" ")[0]),
          Received_Date: parseDate(item?.Received_Date?.split(" ")[0]),
          Billed_Amount: parseAmount(item?.Billed_Amount),
          Paid_Amount: parseAmount(item?.Paid_Amount),
          Mem_Rep: parseAmount(item?.Mem_Rep),
          Member_Id: Number(item?.Member_Id?.replace("-", "")),
        }));

        setResults(formattedData || []);
        setTotalSummary(response?.data.Summary || {});
        setTotalRecords(response?.data.Claims[0]?.TotalRecord || 0);
        setGridLoading(false);
      } catch (err) {
        console.log("-------------------------------------");
        console.log("HANDLE SEARCH2 ERROR : " + err);
        console.log("-------------------------------------");
        setError(
          err.response?.data?.message ||
            "Failed to search claims. Please try again.",
        );
        setResults([]);
        setTotalRecords(0);
        setGridLoading(false);
        setIsItTextSearch(false);
      } finally {
        setGridLoading(false);
      }
    } else {
      toast.error("API not configured.", { title: "Search Claim" });
    }
  };

  const handleClear = () => {
    setSearchBy("0");
    setSearchValue("");
    setSearchFirstName("");
    setSearchLastName("");
    setSearchDob("");
    //setBillingProviderId(user.taxId?.trim());
    setBillingProviderId("");
    setGroupId("");
    setServiceStart("");
    setServiceEnd("");
    setResults([]);
    setSearched(false);
    setLoading(false);
    setError(null);
    setDataState({ skip: 0, take: 10, sort: [], filter: null });
    setExpandedItems([]);
    setSearchText("");
    setTotalRecords(0);
    setSelectedUserId(savedState.selectedUserId);
    claimSearchCache = null; // forget the persisted search
  };

  // When the Search-By category changes, wipe the previous input values
  // so the user doesn't submit stale data in a newly-hidden field.
  const handleSearchByChange = (nextSearchBy) => {
    setSearchBy(nextSearchBy);
    setSearchValue("");
    setSearchFirstName("");
    setSearchLastName("");
    setSearchDob("");
  };

  // Human-readable label + placeholder for the dynamic single-field input.
  const singleFieldMeta = {
    memberId: { label: "Member ID", placeholder: "e.g. SND-100234" },
    claimNumber: { label: "Claim Number", placeholder: "e.g. CLM-2026-0112" },
    accountNumber: { label: "Account Number", placeholder: "e.g. PA-4821037" },
    checkNumber: { label: "Check Number", placeholder: "e.g. CHK-90412" },
  };

  //const onExpandChange = (e) => {
  //    const id = e.dataItem.id;
  //    setExpandedItems((prev) =>
  //        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
  //    );
  //};

  const onExpandChange = async (id) => {
    //alert("onExpandChange : " +id);

    if (isApiConfigured()) {
      try {
        setDetailLoading((prev) => ({ ...prev, [id]: true }));

        //const response = await claimApi.getClaimDetail(
        //    id,
        //    user
        //);

        const response = await claimApi.getClaimDetail({
          billingProvider: billingProviderId,
          claimNo: id,
          checkNo: "",
        });

        setClaimDetails((prev) => ({
          ...prev,
          [id]: response.data,
        }));
      } catch (err) {
        console.error(err);
      } finally {
        setDetailLoading((prev) => ({ ...prev, [id]: false }));
      }
    }
  };

  // Stat computations
  //const totalClaims = results.length;
  //const paidClaims = results.filter((c) => c.status === 'Paid');
  //const pendingClaims = results.filter((c) => c.status === 'Pending');
  //const totalPaidAmount = paidClaims.reduce((sum, c) => sum + (c.paidAmount || 0), 0);

  // Stat computations
  const totalClaims = results.length;
  //const paidClaims = results.filter((c) => c.Status_Description === 'Paid');
  const paidClaims = results.filter((c) => {
    //  const amount = parseFloat((c.Paid_Amount || '0').replace(/[$,]/g, '')) || 0;
    const amount = parseFloat(c.Paid_Amount || "0") || 0;

    return amount > 0;
  });

  const pendingClaims = results.filter((c) => {
    //const amount = parseFloat((c.Paid_Amount || '0').replace(/[$,]/g, '')) || 0;
    const amount = parseFloat(c.Paid_Amount || "0") || 0;

    return amount == 0;
  });
  // const pendingClaims = results.filter((c) => c.Status_Description === 'Pended');
  //const totalPaidAmount = paidClaims.reduce((sum, c) => sum + (c.Paid_Amount || 0), 0);
  const totalPaidAmount = paidClaims.reduce((sum, c) => {
    //const amount = parseFloat(
    //    (c.Paid_Amount || '0').replace(/[$,]/g, '')
    //) || 0;

    const amount = parseFloat(c.Paid_Amount || "0") || 0;

    return sum + amount;
  }, 0);

  // Apply free-text search before processing — matches against every visible
  // field so users get fast narrowing without thinking about which column.
  //const filteredResults = useMemo(() => {
  //    if (!searchText.trim()) return results;
  //    const q = searchText.trim().toLowerCase();
  //    return results.filter((c) => {
  //        return [
  //            c.Claim_Number, c.Member_Name, c.Member_Id, c.Provider_Name,
  //            c.Status_Description, c.Service_Date?.toLocaleDateString('en-US', {
  //                month: '2-digit',
  //                day: '2-digit',
  //                year: 'numeric'
  //            }),
  //            c.Billed_Amount, c.Paid_Amount, c.Ex_Code, c.Check_Number, c.Patient_Account, c.Check_Date?.toLocaleDateString('en-US', {
  //                month: '2-digit',
  //                day: '2-digit',
  //                year: 'numeric'
  //            })
  //        ]
  //            .filter((v) => v !== undefined && v !== null && v !== '')
  //            .some((v) => String(v).toLowerCase().includes(q));
  //    });
  //}, [results, searchText]);

  // Reset to page 0 when search text changes — avoids landing on an empty
  // page after the result set shrinks.
  //useEffect(() => {
  //    setDataState((s) => ({ ...s, skip: 0 }));
  //}, [searchText]);

  useEffect(() => {
    if (isRestoring.current) return;

    setDataState((s) => ({
      ...s,
      skip: 0,
    }));
  }, [searchText]);

  const handleToggle = async (claimNumber) => {
    // If already expanded → collapse it
    if (expandedItems.includes(claimNumber)) {
      setExpandedItems([]);
      return;
    }

    // If data already exists → collapse all and expand only this one
    if (claimDetails[claimNumber]) {
      setExpandedItems([claimNumber]);
      return;
    }

    try {
      //setPageLoading(true);
      setCardDetailLoading(true);
      setClaimDetailsLoading((prev) => ({
        ...prev,
        [claimNumber]: true,
      }));

      // Collapse all previous
      setExpandedItems([]);

      const response = await claimApi.getClaimDetail({
        billingProvider: billingProviderId,
        claimNo: claimNumber,
        checkNo: "",
      });

      if (response.success) {
        const res = response.data;

        const payee = res.Payee?.[0] || {};
        const details = res.Details || [];
        const exCodes = res.ExCodes || [];

        const exMap = Object.fromEntries(
          exCodes.map((e) => [e.Ex_Code, e.Ex_Description]),
        );

        const mappedClaim = {
          claimNumber: details[0]?.Claim_Number || "",
          memberName: details[0]?.Member_Name || "",
          memberId: details[0]?.Member_Id || "",
          patientAccountNo: details[0]?.Account_No || "",
          providerName: payee?.Name || "",
          billingProviderId: payee?.Federal_ID || "",
          status: "Unknown",
          exCode: exCodes.map((x) => x.Ex_Code).join(", "),
          billedAmount: details.reduce(
            (sum, d) =>
              sum +
              (parseFloat((d.Billed_Price || "0").replace(/[$,]/g, "")) || 0),
            0,
          ),
          paidAmount: details.reduce(
            (sum, d) =>
              sum + (parseFloat((d.Paid_Amt || "0").replace(/[$,]/g, "")) || 0),
            0,
          ),
          checkNumber: payee?.Check_Number || "",
          checkDate: payee?.Check_Date || "",
          lineItems: details.map((d, i) => ({
            lineNumber: i + 1,
            serviceDescription: d.Services_Desc,
            procedureCode: d.Procedure_Code,
            billedPrice: parseFloat(d.Billed_Price?.replace(/[$,]/g, "") || 0),
            paidAmount: parseFloat(d.Paid_Amt?.replace(/[$,]/g, "") || 0),
            exCode: d.Ex_Code,
            exDescription: exMap[d.Ex_Code],
          })),
        };

        setClaimDetails((prev) => ({
          ...prev,
          [claimNumber]: mappedClaim,
        }));
        //setPageLoading(false);
        setCardDetailLoading(false);

        // Collapse all previous and expand only current
        setExpandedItems([claimNumber]);
      }
    } catch (err) {
      console.error(err);
      //setPageLoading(false);
      setCardDetailLoading(false);
    } finally {
      setClaimDetailsLoading((prev) => ({
        ...prev,
        [claimNumber]: false,
      }));
      //setPageLoading(false);
      setCardDetailLoading(false);
    }
  };

  //const handleToggle = async (claimNumber) => {
  //    // If already expanded → just collapse (no API call)
  //    if (expandedItems.includes(claimNumber)) {
  //        setExpandedItems((prev) =>
  //            prev.filter((x) => x !== claimNumber)
  //        );
  //        return;
  //    }

  //    // If already fetched → just expand (no API call)
  //    if (claimDetails[claimNumber]) {
  //        setExpandedItems((prev) => [...prev, claimNumber]);
  //        return;
  //    }

  //    try {
  //        // Set loading for THIS card only
  //        setClaimDetailsLoading((prev) => ({
  //            ...prev,
  //            [claimNumber]: true
  //        }));

  //        //const response = await claimApi.getClaimDetail(
  //        //    claimNumber,
  //        //    user
  //        //);

  //        const response = await claimApi.getClaimDetail({
  //            billingProvider: billingProviderId,
  //            claimNo: claimNumber,
  //            checkNo: ""
  //        });

  //        if (response.success) {
  //            const res = response.data;

  //            const payee = res.Payee?.[0] || {};
  //            const details = res.Details || [];
  //            const exCodes = res.ExCodes || [];

  //            const exMap = Object.fromEntries(
  //                exCodes.map(e => [e.Ex_Code, e.Ex_Description])
  //            );

  //            const mappedClaim = {
  //                claimNumber: details[0]?.Claim_Number || '',
  //                memberName: details[0]?.Member_Name || '',
  //                memberId: details[0]?.Member_Id || '',
  //                patientAccountNo: details[0]?.Account_No || '',
  //                providerName: payee?.Name || '',
  //                billingProviderId: payee?.Federal_ID || '',
  //                status: 'Unknown',

  //                exCode: exCodes.map(x => x.Ex_Code).join(', '),

  //                billedAmount: details.reduce((sum, d) => {
  //                    const v = parseFloat((d.Billed_Price || '0').replace(/[$,]/g, '')) || 0;
  //                    return sum + v;
  //                }, 0),

  //                paidAmount: details.reduce((sum, d) => {
  //                    const v = parseFloat((d.Paid_Amt || '0').replace(/[$,]/g, '')) || 0;
  //                    return sum + v;
  //                }, 0),

  //                checkNumber: payee?.Check_Number || '',
  //                checkDate: payee?.Check_Date || '',

  //                lineItems: details.map((d, i) => ({
  //                    lineNumber: i + 1,
  //                    serviceDescription: d.Services_Desc,
  //                    procedureCode: d.Procedure_Code,
  //                    billedPrice: parseFloat(d.Billed_Price?.replace(/[$,]/g, '') || 0),
  //                    paidAmount: parseFloat(d.Paid_Amt?.replace(/[$,]/g, '') || 0),
  //                    exCode: d.Ex_Code,
  //                    exDescription: exMap[d.Ex_Code]
  //                }))
  //            };

  //            setClaimDetails((prev) => ({
  //                ...prev,
  //                [claimNumber]: mappedClaim
  //            }));

  //            // Expand AFTER data is ready
  //            setExpandedItems((prev) => [...prev, claimNumber]);
  //        } else {
  //            toast.error('Claim details not found', { title: 'Claim Details' });
  //        }
  //    } catch (err) {
  //        console.error(err);
  //    } finally {
  //        setClaimDetailsLoading((prev) => ({
  //            ...prev,
  //            [claimNumber]: false
  //        }));
  //    }
  //};

  // Grid data with expansion state — driven by the filtered (post-search) set.
  //const gridData = process(
  //    filteredResults.map((item) => ({
  //        ...item,
  //        expanded: expandedItems.includes(item.id),
  //    })),
  //    dataState
  //);
  const gridData = {
    data: results,
    total: totalRecords,
  };

  //const gridData = process(
  //    filteredResults.map((item) => ({
  //        ...item,
  //        expanded: expandedItems.includes(item.Claim_Number),
  //    })),
  //    dataState
  //);

  // Custom cell components
  const EOPCell = (props) => {
    const value = props.dataItem.Claim_Number;
    return (
      <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
        {value ? (
          <span
            style={{
              color: "#0D9488",
              fontWeight: 600,
              cursor: "pointer",
              textDecoration: "none",
            }}
            onClick={() => alert(`View EOP: ${value}`)}
            onMouseOver={(e) => {
              e.target.style.textDecoration = "underline";
            }}
            onMouseOut={(e) => {
              e.target.style.textDecoration = "none";
            }}
          >
            {value}
          </span>
        ) : (
          <span style={{ color: "#9CA3AF" }}>--</span>
        )}
      </td>
    );
  };

  const ClaimNumberCell = (props) => {
    const dataItem = props.dataItem;
    return (
      <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
        <Link
          to={`/claims/${dataItem.Claim_Number}/${dataItem.Check_Number}/${billingProviderId}`}
          state={{
            results,
            totalRecords,
            dataState,
            viewMode,
            searchText,
            expandedItems,
            claimDetails,
            billingProviderId,
            groupId,
            searchBy,
            searchValue,
            searchFirstName,
            searchLastName,
            searchDob,
            serviceStart,
            serviceEnd,
            searched,
            selectedUserId,
            totalSummary,
          }}
          style={{ color: "#0D9488", fontWeight: 600, textDecoration: "none" }}
          onMouseOver={(e) => {
            e.target.style.textDecoration = "underline";
          }}
          onMouseOut={(e) => {
            e.target.style.textDecoration = "none";
          }}
        >
          {dataItem.Claim_Number}
        </Link>
      </td>
    );
  };

  const BilledAmountCell = (props) => {
    const value = props.dataItem.Billed_Amount;
    return (
      <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
        <span style={{ fontWeight: 600 }}>
          {/*${typeof value === 'number' ? value.toFixed(2) : '0.00'}*/}
          {value}
        </span>
      </td>
    );
  };

  const PaidAmountCell = (props) => {
    const value = props.dataItem.Paid_Amount;
    return (
      <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
        <span
          style={{ fontWeight: 600, color: value > 0 ? "#059669" : "#6B7280" }}
        >
          {/*${typeof value === 'number' ? value.toFixed(2) : '0.00'}*/}
          {value}
        </span>
      </td>
    );
  };

  const StatusCell = (props) => {
    const value = props.dataItem.Status_Description;
    const badgeClass = value.toLowerCase();
    return (
      <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
        <span className={`status-badge ${badgeClass}`}>{value}</span>
      </td>
    );
  };

  const CheckNumberCell = (props) => {
    const value = props.dataItem.Check_Number;
    return (
      <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
        {value || <span style={{ color: "#9CA3AF" }}>--</span>}
      </td>
    );
  };

  const CheckDateCell = (props) => {
    //const value = props?.dataItem?.Check_Date?.toLocaleDateString('en-US');
    const value = props?.dataItem?.Check_Date?.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
    return (
      <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
        {value || <span style={{ color: "#9CA3AF" }}>--</span>}
      </td>
    );
  };

  const ServiceDateCell = (props) => {
    const value = props?.dataItem?.Service_Date?.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
    // const value = props?.dataItem?.Service_Date?.toLocaleDateString('en-US');
    return (
      <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
        {value || <span style={{ color: "#9CA3AF" }}>--</span>}
      </td>
    );
  };

  const ReceivedDateCell = (props) => {
    const value = props?.dataItem?.Received_Date?.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });

    return (
      <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
        {value || <span style={{ color: "#9CA3AF" }}>--</span>}
      </td>
    );
  };

  const ExCodeCell = (props) => {
    const value = props.dataItem.Ex_Code;
    return (
      <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
        {value || <span style={{ color: "#9CA3AF" }}>--</span>}
      </td>
    );
  };

  //const DetailComponent = (props) => {
  //    const claim = props.dataItem;
  //    return (
  //        <div style={{ padding: '16px 24px', background: 'rgba(13,148,136,0.03)', borderLeft: '3px solid #0D9488' }}>
  //            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
  //                <div style={{ fontSize: 15, fontWeight: 700, color: '#1A1D21' }}>Claim Line Items</div>
  //                <Link to={`/claims/${claim.id}`} style={{ fontSize: 13, fontWeight: 600, color: '#0D9488', textDecoration: 'none' }}>
  //                    View Full Details →
  //                </Link>
  //            </div>
  //            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
  //                <thead>
  //                    <tr style={{ background: '#F1F5F9', textTransform: 'uppercase', fontSize: 11, fontWeight: 600, color: '#6B7280' }}>
  //                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>#</th>
  //                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Service</th>
  //                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Procedure</th>
  //                        <th style={{ padding: '8px 12px', textAlign: 'right' }}>Billed</th>
  //                        <th style={{ padding: '8px 12px', textAlign: 'right' }}>Paid</th>
  //                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>EX Code</th>
  //                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Description</th>
  //                    </tr>
  //                </thead>
  //                <tbody>
  //                    {(claim.lineItems || []).map((li) => (
  //                        <tr key={li.lineNumber} style={{ borderBottom: '1px solid #E8ECF0' }}>
  //                            <td style={{ padding: '8px 12px' }}>{li.lineNumber}</td>
  //                            <td style={{ padding: '8px 12px' }}>{li.serviceDescription}</td>
  //                            <td style={{ padding: '8px 12px' }}>{li.procedureCode}</td>
  //                            <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>${li.billedPrice.toFixed(2)}</td>
  //                            <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>${li.paidAmount.toFixed(2)}</td>
  //                            <td style={{ padding: '8px 12px' }}>{li.exCode}</td>
  //                            <td style={{ padding: '8px 12px', color: '#6B7280' }}>{li.exDescription}</td>
  //                        </tr>
  //                    ))}
  //                </tbody>
  //            </table>
  //        </div>
  //    );
  //};

  const styles = {
    overlay: {
      position: "fixed",
      inset: 0, // replaces top/left/width/height
      backgroundColor: "rgba(255,255,255,0.6)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 9999,
    },
    gridOverlay: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(255,255,255,0.6)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1000,
    },
  };

  const DetailComponent = (props) => {
    const id = props.dataItem.Claim_Number;

    const loading = detailLoading[id];
    const data = claimDetails[id];

    if (loading) {
      //return <div style={{ padding: 16 }}>Loading...</div>;

      return (
        <div style={styles.overlay}>
          {/*<Loader size="large" type="infinite-spinner" />*/}
          <LoadingSpinner message="Loading Claim Details" fullPage="true" />
        </div>
      );
    }

    if (!data) {
      return <div style={{ padding: 16 }}>No details found</div>;
    }

    return (
      <div
        style={{
          padding: "16px 24px",
          background: "rgba(13,148,136,0.03)",
          borderLeft: "3px solid #0D9488",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 700, color: "#1A1D21" }}>
            Claim Line Items
          </div>
          <Link
            to={`/claims/${props.dataItem.Claim_Number}/${props.dataItem.Check_Number}/${billingProviderId}`}
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "#0D9488",
              textDecoration: "none",
            }}
            state={{
              results,
              totalRecords,
              dataState,
              viewMode,
              searchText,
              expandedItems,
              claimDetails,
              billingProviderId,
              groupId,
              searchBy,
              searchValue,
              searchFirstName,
              searchLastName,
              searchDob,
              serviceStart,
              serviceEnd,
              searched,
              selectedUserId,
              totalSummary,
            }}
          >
            View Full Details →
          </Link>
        </div>
        <table
          style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}
        >
          <thead>
            <tr
              style={{
                background: "#F1F5F9",
                textTransform: "uppercase",
                fontSize: 11,
                fontWeight: 600,
                color: "#6B7280",
              }}
            >
              <th style={{ padding: "8px 12px", textAlign: "left" }}>#</th>
              <th style={{ padding: "8px 12px", textAlign: "left" }}>
                Service
              </th>
              <th style={{ padding: "8px 12px", textAlign: "left" }}>
                Procedure
              </th>
              <th style={{ padding: "8px 12px", textAlign: "right" }}>
                Billed
              </th>
              <th style={{ padding: "8px 12px", textAlign: "right" }}>Paid</th>
              <th style={{ padding: "8px 12px", textAlign: "left" }}>
                EX Code
              </th>
              <th style={{ padding: "8px 12px", textAlign: "left" }}>
                Description
              </th>
            </tr>
          </thead>
          <tbody>
            {(data.Details || []).map((li) => (
              <tr
                key={li.Claim_Seq}
                style={{ borderBottom: "1px solid #E8ECF0" }}
              >
                <td style={{ padding: "8px 12px" }}>{li.Claim_Seq}</td>

                <td style={{ padding: "8px 12px" }}>{li.Services_Desc}</td>
                <td style={{ padding: "8px 12px" }}>{li.Procedure_Code}</td>
                <td
                  style={{
                    padding: "8px 12px",
                    textAlign: "right",
                    fontWeight: 600,
                  }}
                >
                  {li.Billed_Price}
                </td>
                <td
                  style={{
                    padding: "8px 12px",
                    textAlign: "right",
                    fontWeight: 600,
                  }}
                >
                  {li.Paid_Amt}
                </td>
                <td style={{ padding: "8px 12px" }}>{li.Ex_Code}</td>
                <td style={{ padding: "8px 12px", color: "#6B7280" }}>
                  {li.Ex_Description}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="main-content">
      {pageLoading && (
        <div style={styles.overlay}>
          <LoadingSpinner
            data-cy="page-loader-profile"
            message="Please wait..."
            fullPage="true"
          />
        </div>
      )}
      <div className="page-header">
        <div>
          <h1>Claim Status</h1>
          <p>Submit claim request and receive results in real time</p>
        </div>

        {selectedUserId && (
          <>
            <div>
              <div
                style={{ display: "flex", gap: 10, justifyContent: "center" }}
              >
                <button
                  onClick={() => navigate("/appeals")}
                  className="btn-primary"

                  style={{
                    padding: "10px 20px",
                    borderRadius: 12,
                    border: "1px solid rgba(255,255,255,0.3)",
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  &#8592; Go Back to Appeal
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Stat Cards */}
      {searched && results.length > 0 && (
        <div className="stat-cards">
          <div className="stat-card">
            <div className="stat-icon teal">
              <svg
                width="22"
                height="22"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <div className="stat-value">{totalSummary?.TotalClaims}</div>
            <div className="stat-label">Total Claims</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon green">
              <svg
                width="22"
                height="22"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="stat-value">{totalSummary?.TotalPaidClaims}</div>
            <div className="stat-label">Paid</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon orange">
              <svg
                width="22"
                height="22"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="stat-value">{totalSummary?.TotalPendingClaims}</div>
            <div className="stat-label">Pending</div>
          </div>

          <div className="stat-card" style={{ overflowX: "auto" }}>
            <div className="stat-icon purple">
              <svg
                width="22"
                height="22"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="stat-value">
              $
              {totalSummary?.TotalPaidAmount?.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
            <div className="stat-label">Total Paid</div>
          </div>
        </div>
      )}

      {/* Search Form */}
      <div className="card">
        <h3>Search Claims</h3>
        <div className="form-grid">
          {
            //user?.userRole?.trim().toLowerCase() !== 'super admin' && (
            !["super admin", "admin"].includes(
              user?.userRole?.trim().toLowerCase(),
            ) && (
              <div className="form-group">
                <label className="required">Billing Provider ID</label>
                <select
                  value={billingProviderId}
                  onChange={(e) => setBillingProviderId(e.target.value)}
                >
                  <option value="">Select Billing Provider ID</option>
                  {userTaxIds?.map((id, index) => (
                    <option key={id} value={id}>
                      {id}
                    </option>
                  ))}
                </select>
              </div>
            )
          }
          {
            //(user?.userRole?.trim().toLowerCase() === 'super admin' && selectedUserId) && (
            ["super admin", "admin"].includes(
              user?.userRole?.trim().toLowerCase(),
            ) &&
              selectedUserId && (
                <div className="form-group">
                  <label className="required">Billing Provider ID</label>
                  <select
                    value={billingProviderId}
                    onChange={(e) => setBillingProviderId(e.target.value)}
                  >
                    <option value="">Select Billing Provider ID</option>
                    {userTaxIds?.map((id, index) => (
                      <option key={id} value={id}>
                        {id}
                      </option>
                    ))}
                  </select>
                </div>
              )
          }
          {
            //(user?.userRole?.trim().toLowerCase() === 'super admin' && !selectedUserId) && (
            ["super admin", "admin"].includes(
              user?.userRole?.trim().toLowerCase(),
            ) &&
              !selectedUserId && (
                <div className="form-group">
                  <label className="required">Billing Provider</label>
                  <input
                    name="txtBillingProvider"
                    type="text"
                    value={billingProviderId}
                    onChange={(e) => setBillingProviderId(e.target.value)}
                    placeholder="Enter Billing Provider ID"
                  />
                </div>
              )
          }

          <div className="form-group">
            <label>Group ID</label>
            <select
              value={groupId}
              name="selGroupId"
              onChange={(e) => setGroupId(e.target.value)}
            >
              <option value="">All Groups</option>
              {groups.map((group) => (
                <option key={group.Group_Id} value={group.Group_Id}>
                  {group.Group_Id}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Search By</label>
            <select
              name="selSearchBy"
              value={searchBy}
              onChange={(e) => handleSearchByChange(e.target.value)}
            >
              <option value="0">Select Search By </option>
              <option value="memberId">Member ID</option>
              <option value="claimNumber">Claim Number</option>
              <option value="accountNumber">Account Number</option>
              <option value="checkNumber">Check Number</option>
              <option value="firstLastDob">
                First Name, Last Name &amp; DOB
              </option>
            </select>
          </div>

          {/* ── Dynamic input based on Search By ─────────────────────
              Single-field types (Member ID / Claim # / Account # / Check #)
              render a single input; "First Name, Last Name & DOB" renders
              three inputs spanning both columns. */}
          {singleFieldMeta[searchBy] && (
            <div className="form-group">
              <label className="required">
                {singleFieldMeta[searchBy].label}
              </label>
              <input
                name="txtSearchText"
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder={singleFieldMeta[searchBy].placeholder}
              />
            </div>
          )}

          {searchBy === "firstLastDob" && (
            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
              <label className="required">
                Member Name &amp; Date of Birth
              </label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                  gap: 12,
                }}
              >
                <input
                  name="txtFirstName"
                  type="text"
                  value={searchFirstName}
                  onChange={(e) => setSearchFirstName(e.target.value)}
                  placeholder="First Name"
                  autoComplete="off"
                />
                <input
                  name="txtLastName"
                  type="text"
                  value={searchLastName}
                  onChange={(e) => setSearchLastName(e.target.value)}
                  placeholder="Last Name"
                  autoComplete="off"
                />
                <input
                  name="dateDOB"
                  type="date"
                  value={searchDob}
                  onChange={(e) => setSearchDob(e.target.value)}
                  aria-label="Date of Birth"
                />
              </div>
            </div>
          )}

          {/*<div className="form-group">*/}
          {/*    <label>Billing Provider ID</label>*/}
          {/*    <input*/}
          {/*    name="txtBillingProvider"*/}
          {/*        type="text"*/}
          {/*        value={billingProviderId}*/}
          {/*        onChange={(e) => setBillingProviderId(e.target.value)}*/}
          {/*        placeholder="Enter Billing Provider ID"*/}
          {/*    />*/}
          {/*</div>*/}

          {/* Service date range — always its own row so Start and End sit
                        side by side regardless of which Search-By input is showing. */}
          <div
            style={{
              gridColumn: "1 / -1",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 20,
            }}
          >
            <div className="form-group">
              <label>Service Start</label>
              <input
                name="dateServiceStart"
                type="date"
                value={serviceStart}
                max={serviceEnd || undefined}
                onChange={(e) => setServiceStart(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Service End</label>
              <input
                name="dateServiceEnd"
                type="date"
                value={serviceEnd}
                min={serviceStart || undefined}
                onChange={(e) => setServiceEnd(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button
            name="btnSearch"
            className="btn-primary"
            //  onClick={handleSearch}
            onClick={() =>
              handleSearch({
                ...dataState,
                skip: 0,
              })
            }
            disabled={loading}
            aria-busy={loading}
          >
            {loading ? "Searching…" : "Search"}
          </button>
          <button
            name="btnClear"
            className="btn-secondary"
            onClick={handleClear}
            disabled={loading}
          >
            Clear
          </button>
        </div>
      </div>

      {/* Loading & Error States 
            {loading && (
                <div className="card">
                    <LoadingSpinner message="Searching claims..." />
                </div>
            )}

            {error && (
                <ErrorCard message={error} onRetry={handleSearch} />
            )}

            {/* Grid Toolbar + Results Grid */}
      {searched &&
      !loading &&
      !error &&
      !isItTextSearch &&
      results.length === 0 ? (
        <div
          className="card"
          style={{ textAlign: "center", padding: "48px 24px" }}
        >
          <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.5 }}>
            <Search size={36} color="#9CA3AF" strokeWidth={1.5} />
          </div>
          <div
            style={{
              fontSize: 18,
              fontWeight: 600,
              color: "var(--text-primary)",
              marginBottom: 4,
            }}
          >
            No Claims Found
          </div>
          <div style={{ fontSize: 14, color: "var(--text-secondary)" }}>
            Try adjusting your search criteria or date range
          </div>
        </div>
      ) : (
        <>
          {searched && !loading && !error && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 12,
                  padding: "0 2px",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                {/*<span style={{ fontSize: 14, color: '#6B7280', fontWeight: 500 }}>*/}
                {/*    Showing {filteredResults.length} of {results.length} claim{results.length !== 1 ? 's' : ''}*/}
                {/*</span>*/}
                <span
                  style={{ fontSize: 14, color: "#6B7280", fontWeight: 500 }}
                >
                  {/*Showing {dataState.skip + 1} - {Math.min(dataState.skip + results.length, totalRecords)} of {totalRecords} claim{totalRecords !== 1 ? 's' : ''}*/}
                  {totalRecords === 0
                    ? "Showing 0 of 0 claims"
                    : `Showing ${dataState.skip + 1} - ${Math.min(dataState.skip + results.length, totalRecords)} of ${totalRecords} claim${totalRecords !== 1 ? "s" : ""}`}
                </span>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    flexWrap: "wrap",
                  }}
                >
                  {/* Free-text search — works in both Cards and Table views */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "6px 10px",
                      background: "#fff",
                      border: "1px solid #E8ECF0",
                      borderRadius: 10,
                      minWidth: 220,
                    }}
                  >
                    <Search size={14} color="#94A3B8" />
                    <input
                      name="txtSearch"
                      type="text"
                      value={searchText}
                      /*onChange={(e) => setSearchText(e.target.value)}*/
                      /*onChange={(e) => {*/
                      /*    const value = e.target.value;*/

                      /*    setSearchText(value);*/

                      /*    const newState = {*/
                      /*        ...dataState,*/
                      /*        skip: 0*/
                      /*    };*/

                      /*    setDataState(newState);*/

                      /*    handleSearch2(newState, value);*/
                      /*}}*/
                      onChange={(e) => {
                        const value = e.target.value;

                        setSearchText(value);

                        const newState = {
                          ...dataState,
                          skip: 0,
                        };

                        setDataState(newState);

                        // Clear previous timer
                        if (searchTimeoutRef.current) {
                          clearTimeout(searchTimeoutRef.current);
                        }

                        // Set new timer (3 seconds)
                        searchTimeoutRef.current = setTimeout(() => {
                          handleSearch2(newState, value);
                        }, 3000);
                      }}
                      placeholder="Search results…"
                      style={{
                        flex: 1,
                        border: "none",
                        outline: "none",
                        fontSize: 13,
                        color: "#1A1D21",
                        background: "transparent",
                        fontFamily: "inherit",
                        minWidth: 0,
                      }}
                    />
                    {searchText && (
                      <button
                        name="btnClearSearch"
                        //onClick={() => setSearchText('')}
                        onClick={() => {
                          const newState = {
                            ...dataState,
                            skip: 0,
                          };

                          setSearchText("");
                          setDataState(newState);

                          handleSearch2(newState, "");
                        }}
                        style={{
                          border: "none",
                          background: "transparent",
                          cursor: "pointer",
                          padding: 0,
                          color: "#94A3B8",
                          display: "inline-flex",
                        }}
                        title="Clear search"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      background: "#F1F5F9",
                      borderRadius: 10,
                      padding: 3,
                      gap: 2,
                    }}
                  >
                    <button
                      onClick={() => {
                        setViewMode("cards");
                        setExpandedItems([]);
                        setClaimDetails({});
                        //setClaimDetails([]);
                      }}
                      style={toggleBtnStyle(viewMode === "cards")}
                      title="Card view"
                      aria-pressed={viewMode === "cards"}
                    >
                      <LayoutGrid size={14} strokeWidth={2.5} /> Cards
                    </button>
                    <button
                      // onClick={() => setViewMode('table')}
                      onClick={() => {
                        setViewMode("table");
                        setExpandedItems([]);
                        setClaimDetails({});
                        // setClaimDetails([]);
                      }}
                      style={toggleBtnStyle(viewMode === "table")}
                      title="Table view"
                      aria-pressed={viewMode === "table"}
                    >
                      <List size={14} strokeWidth={2.5} /> Table
                    </button>
                  </div>
                  <Button
                    themeColor="primary"
                    fillMode="outline"
                    onClick={async () => {
                      if (excelExportRef.current) {
                        // Export the filtered (post-search) result set so what
                        // the user sees is exactly what gets exported.
                        //excelExportRef.current.save(filteredResults);
                        //excelExportRef.current.save(gridData.data);
                        const newState = {
                          ...dataState,
                          skip: 0,
                        };

                        const exportData = await exportToExcel(newState, "");

                        //console.log("Data  get at export to excel function : " + JSON.stringify(exportData));
                        excelExportRef.current.save(exportData);
                      }
                    }}
                    style={{ fontSize: 13 }}
                  >
                    {IsExportingToExcel
                      ? "Exporting to Excel..."
                      : "Export to Excel"}
                  </Button>
                </div>
              </div>

              {/* Excel export is attached even in card view so the Export button works */}
              <ExcelExport ref={excelExportRef} fileName="ClaimStatus_Export">
                {/*<ExcelExportColumn field="eopNumber" title="EOP Request" width={160} />*/}
                <ExcelExportColumn
                  field="Service_Date"
                  title="Service Date"
                  width={130}
                />
                <ExcelExportColumn
                  field="Claim_Number"
                  title="Claim #"
                  width={160}
                />
                <ExcelExportColumn
                  field="Patient_Account"
                  title="Patient Account"
                  width={160}
                />
                <ExcelExportColumn
                  field="Member_Name"
                  title="Member Name"
                  width={170}
                />
                <ExcelExportColumn
                  field="Member_Id"
                  title="Member ID"
                  width={140}
                />
                <ExcelExportColumn
                  field="Billed_Amount"
                  title="Billed Amount"
                  width={130}
                />
                <ExcelExportColumn
                  field="Paid_Amount"
                  title="Paid Amount"
                  width={120}
                />
                <ExcelExportColumn
                  field="Status_Description"
                  title="Status"
                  width={150}
                />
                <ExcelExportColumn
                  field="Check_Number"
                  title="Check #"
                  width={130}
                />
                <ExcelExportColumn
                  field="Check_Date"
                  title="Check Date"
                  width={130}
                />
                {/*<ExcelExportColumn field="exCode" title="EX Code" width={100} />*/}
              </ExcelExport>

              <div style={{ position: "relative" }}>
                {gridLoading && (
                  <div style={styles.gridOverlay}>
                    <LoadingSpinner
                      data-cy="grid-loader"
                      message="Loading claims..."
                    />
                  </div>
                )}

                {cardDetailLoading && (
                  <div style={styles.gridOverlay}>
                    <LoadingSpinner
                      data-cy="grid-loader"
                      message="Loading Details...."
                    />
                  </div>
                )}

                {viewMode === "cards" ? (
                  //results.length === 0 ? (
                  totalRecords === 0 ? (
                    <div
                      className="card"
                      style={{ textAlign: "center", padding: "48px 24px" }}
                    >
                      <div style={{ fontSize: 14, color: "#6B7280" }}>
                        No claims match your search.
                      </div>
                    </div>
                  ) : (
                    <>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fill, minmax(280px, 1fr))",
                          gap: 16,
                        }}
                      >
                        {/* gridData.data is the post-search/sort/page slice — Cards
                      view uses it so paging behaves consistently. */}
                        {gridData.data.map((c) => (
                          //<ClaimCard
                          //    key={c.Claim_Number}
                          //    claim={c}
                          //    expanded={expandedItems.includes(c.Claim_Number)}
                          //    onToggle={() => handleToggle(c.Claim_Number)}
                          //    claimDetails={claimDetails[c.Claim_Number]}
                          //    loading={claimDetailsLoading[c.Claim_Number]}
                          //    billingProviderId={ billingProviderId }
                          ///>

                          <ClaimCard
                            key={c.Claim_Number}
                            claim={c}
                            expanded={expandedItems.includes(c.Claim_Number)}
                            onToggle={() => handleToggle(c.Claim_Number)}
                            claimDetails={claimDetails[c.Claim_Number]}
                            //  claimDetails={claimDetails}
                            loading={claimDetailsLoading[c.Claim_Number]}
                            billingProviderId={billingProviderId}
                            results={results}
                            totalRecords={totalRecords}
                            dataState={dataState}
                            viewMode={viewMode}
                            searchText={searchText}
                            expandedItems={expandedItems}
                            groupId={groupId}
                            searchBy={searchBy}
                            searchValue={searchValue}
                            searchFirstName={searchFirstName}
                            searchLastName={searchLastName}
                            searchDob={searchDob}
                            serviceStart={serviceStart}
                            serviceEnd={serviceEnd}
                            searched={searched}
                            selectedUserId={selectedUserId}
                            totalSummary={totalSummary}
                          />
                        ))}
                      </div>
                      <CardsPagination
                        //total={gridData.total}
                        total={totalRecords}
                        skip={dataState.skip}
                        take={dataState.take}
                        //    onPageChange={({ skip, take }) => setDataState((s) => ({ ...s, skip, take }))}
                        onPageChange={({ skip, take }) => {
                          const newState = {
                            ...dataState,
                            skip,
                            take,
                          };

                          setDataState(newState);
                          handleSearch2(newState, searchText);
                        }}
                        pageSizes={[5, 10, 20]}
                      />
                    </>
                  )
                ) : (
                  <div
                    className="card"
                    style={{ padding: 0, overflow: "hidden" }}
                  >
                    <div className="grid-scroll-wrapper">
                      <Grid
                        //  scrollable="none"
                        scrollable="scrollable"
                        //data={gridData}
                        //sortable={true}
                        resizable={true}
                        reorderable={true}
                        //pageable={{ pageSizes: [10, 20, 50] }}
                        filterable={false}
                        data={gridData.data}
                        total={gridData.total}
                        skip={dataState.skip}
                        take={dataState.take}
                        sort={dataState.sort}
                        // filter={dataState.filter}
                        sortable={true}

                        pageable={{ pageSizes: [5, 10, 20] }}
                        onDataStateChange={(e) => {
                          setDataState(e.dataState);
                          handleSearch2(e.dataState, searchText);
                        }}
                        loading={gridLoading}
                        dataItemKey="Claim_Number"
                        detail={DetailComponent}
                        expandField="expanded"
                        onDetailExpandChange={(e) => {
                          //console.log("EVENT:", e);
                          //console.log("Keys:", Object.keys(e));

                          e.detailExpand[expandedItems] = false;

                          const found = Object.entries(e.detailExpand).find(
                            ([id, value]) => value === true,
                          );

                          const id = found?.[0]; // safe access

                          const isValidId =
                            id !== null &&
                            id !== undefined &&
                            String(id).trim() !== "";

                          if (isValidId) {
                            // setExpandedItems(id);
                            setExpandedItems([id]);

                            //console.log("==========e.detailExpand=============");
                            //console.log(e.detailExpand);
                            //console.log("============id==============");
                            //console.log(id);
                            //console.log("EXPANDED ITEMS OBJECT AT ONEXPAND CHANGE" + expandedItems);
                            //console.log("===========================");

                            if (!claimDetails[id]) {
                              onExpandChange(id);
                            }
                          } else {
                            //console.log("==========e.detailExpand=============");
                            //console.log(e.detailExpand);
                            //console.log("============id==============");
                            //console.log(id);
                            //console.log("===========================");
                            //alert("NOT VALID ID :" + id);
                            //setExpandedItems([]);
                            //setClaimDetails([]);

                            setExpandedItems([]);
                            setClaimDetails({});
                          }
                        }}

                        /*onDataStateChange={(e) => setDataState(e.dataState)}*/
                        /*{...dataState}*/
                        style={{ border: "none" }}
                      >
                        {/*<GridColumn*/}
                        {/*    field="Claim_Number"*/}
                        {/*    title="EOP Request"*/}
                        {/*    width="160px"*/}
                        {/*    columnMenu={ColumnMenu}*/}
                        {/*    cells={{ data: EOPCell }}*/}
                        {/*/>*/}
                        <GridColumn
                          field="Service_Date"
                          title="Service Date"
                          width="130px"
                          columnMenu={ColumnMenuClaim}
                          format="{0:MM/dd/yyyy}"
                          cells={{ data: ServiceDateCell }}
                          filter="date"
                        />
                        <GridColumn
                          field="Received_Date"
                          title="Received Date"
                          width="130px"
                          columnMenu={ColumnMenuClaim}
                          format="{0:MM/dd/yyyy}"
                          cells={{ data: ReceivedDateCell }}
                          filter="date"
                        />
                        <GridColumn
                          field="Claim_Number"
                          title="Claim #"
                          width="160px"
                          columnMenu={ColumnMenuClaim}
                          cells={{ data: ClaimNumberCell }}
                        />
                        <GridColumn
                          field="Patient_Account"
                          title="Patient Account"
                          width="160px"
                          columnMenu={ColumnMenuClaim}
                        />
                        <GridColumn
                          field="Member_Name"
                          title="Member Name"
                          width="170px"
                          columnMenu={ColumnMenuClaim}
                        />
                        <GridColumn
                          field="Member_Id"
                          title="Member ID"
                          width="140px"
                          columnMenu={ColumnMenuClaim}
                        />
                        <GridColumn
                          field="Billed_Amount"
                          title="Billed Amount($)"
                          width="130px"
                          columnMenu={ColumnMenuClaim}
                          cells={{ data: BilledAmountCell }}
                        />
                        <GridColumn
                          field="Paid_Amount"
                          title="Paid Amount($)"
                          width="120px"
                          columnMenu={ColumnMenuClaim}
                          cells={{ data: PaidAmountCell }}
                        />
                        <GridColumn
                          field="Status_Description"
                          title="Status"
                          width="150px"
                          columnMenu={ColumnMenuClaim}
                          cells={{ data: StatusCell }}
                        />
                        <GridColumn
                          field="Check_Number"
                          title="Check #"
                          width="130px"
                          columnMenu={ColumnMenuClaim}
                          cells={{ data: CheckNumberCell }}
                        />
                        <GridColumn
                          field="Check_Date"
                          title="Check Date"
                          width="130px"
                          columnMenu={ColumnMenuClaim}
                          cells={{ data: CheckDateCell }}
                          format="{0:MM/dd/yyyy}"
                          filter="date"
                        />
                        <GridColumn
                          field="Ex_Code"
                          title="EX Code"
                          width="100px"
                          columnMenu={ColumnMenuClaim}
                          cells={{ data: ExCodeCell }}
                        />
                      </Grid>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/*{searched && !loading && !error && (*/}

      {/* Info Banner */}
      {/*<div*/}
      {/*    className="card"*/}
      {/*    style={{*/}
      {/*        background: 'rgba(13, 148, 136, 0.05)',*/}
      {/*        borderLeft: '4px solid #0D9488',*/}
      {/*        display: 'flex',*/}
      {/*        alignItems: 'center',*/}
      {/*        gap: 12,*/}
      {/*        marginTop:'5px'*/}
      {/*    }}*/}
      {/*>*/}
      {/*    */}
      {/*<span style={{ fontSize: 20 }}>i</span>*/}
      {/*    */}
      {/*<p style={{ margin: 0, fontSize: 14, color: '#134E4A' }}>*/}
      {/*    */}
      {/*    For terminated members, eligibility must be verified via the ACHP*/}
      {/*    */}
      {/*    Provider Portal.*/}
      {/*    */}
      {/*</p>*/}
      {/*</div>*/}
    </div>
  );
};

/* ── Card view for claims ──────────────────────────────────── */
//const ClaimCard = ({ claim, expanded, onToggle, claimDetails}) => {
//const ClaimCard = ({ claim, expanded, onToggle, claimDetails, loading, billingProviderId })
const ClaimCard = ({
  claim,
  expanded,
  onToggle,
  claimDetails,
  loading,
  billingProviderId,
  results,
  totalRecords,
  dataState,
  viewMode,
  searchText,
  expandedItems,
  groupId,
  searchBy,
  searchValue,
  searchFirstName,
  searchLastName,
  searchDob,
  serviceStart,
  serviceEnd,
  searched,
  selectedUserId,
  totalSummary,
}) => {
  // alert("claimDetails : "+JSON.stringify(claimDetails));

  // console.log("claimDetails AT CLAIM CARD : " + JSON.stringify(claimDetails));
  const status = (claim.Status_Description || "").toLowerCase();
  // const billed = typeof claim.billedAmount === 'number' ? claim.billedAmount : 0;
  const billed = claim.Billed_Amount ? claim.Billed_Amount : 0;
  const paid = claim.Paid_Amount ? claim.Paid_Amount : 0;

  return (
      <div
          data-cy="claimCard"
      style={{
        background: "#fff",
        border: "1px solid #E8ECF0",
        borderRadius: 16,
        padding: 18,
        transition: "all 0.2s",
        boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "#CBD5E1";
        e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.06)";
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "#E8ECF0";
        e.currentTarget.style.boxShadow = "0 1px 2px rgba(0,0,0,0.03)";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      {/* Header: claim # + status */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <Link
          name="linkClaimDetailsCard"
          /*to={`/claims/${claim.Claim_Number}`}*/
          to={`/claims/${claim.Claim_Number}/${claim.Check_Number}/${billingProviderId}`}
          state={{
            results,
            totalRecords,
            dataState,
            viewMode,
            searchText,
            expandedItems,
            claimDetails,
            billingProviderId,
            groupId,
            searchBy,
            searchValue,
            searchFirstName,
            searchLastName,
            searchDob,
            serviceStart,
            serviceEnd,
            searched,
            selectedUserId,
            totalSummary,
          }}
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: "#0D9488",
            textDecoration: "none",
            fontFamily: "monospace",
          }}
          onMouseOver={(e) => (e.target.style.textDecoration = "underline")}
          onMouseOut={(e) => (e.target.style.textDecoration = "none")}
        >
          {claim.Claim_Number}
        </Link>
        <span className={`status-badge ${status}`}>
          {claim.Status_Description}
        </span>
      </div>

      {/* Member */}
      <div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 15,
            fontWeight: 700,
            color: "#1A1D21",
            marginBottom: 2,
          }}
        >
          <User size={14} color="#6B7280" strokeWidth={2} />
          {claim.Member_Name}
        </div>
        <div
          style={{ fontSize: 12, color: "#6B7280", fontFamily: "monospace" }}
        >
          {claim.Member_Id}
        </div>
      </div>

      {/* Amounts */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 8,
          padding: "10px 12px",
          background: "#F8FAFB",
          borderRadius: 10,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: "#6B7280",
              textTransform: "uppercase",
              letterSpacing: 0.5,
              marginBottom: 2,
            }}
          >
            Billed
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#1A1D21" }}>
            {" "}
            {`${billed < 0 ? "-" : ""}$${Math.abs(billed).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          </div>
        </div>
        <div>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: "#6B7280",
              textTransform: "uppercase",
              letterSpacing: 0.5,
              marginBottom: 2,
            }}
          >
            Paid
          </div>
          {/*<div style={{ fontSize: 14, fontWeight: 700, color: parseFloat(paid.replace(/[$,]/g, ''))  > 0 ? '#059669' : '#9CA3AF' }}>{paid}</div>*/}
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: parseFloat(paid) > 0 ? "#059669" : "#9CA3AF",
            }}
          >{`${paid < 0 ? "-" : ""}$${Math.abs(paid).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</div>
        </div>
      </div>

      {/* Service date */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 12,
          color: "#6B7280",
        }}
      >
        <Calendar size={12} strokeWidth={2} />
        <span
          style={{ fontWeight: 600 }}
        >{`Service\u00A0\u00A0\u00A0\u00A0:`}</span>
        {/*<span style={{ color: '#1A1D21' }}>{claim?.Service_Date?.split(' ')[0]}</span>*/}
        <span style={{ color: "#1A1D21" }}>
          {claim?.Service_Date?.toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
          })}
        </span>
      </div>

      {/* Received date */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 12,
          color: "#6B7280",
        }}
      >
        <Calendar size={12} strokeWidth={2} />
        <span style={{ fontWeight: 600 }}>Received :</span>
        {/*<span style={{ color: '#1A1D21' }}>{claim?.Service_Date?.split(' ')[0]}</span>*/}
        <span style={{ color: "#1A1D21" }}>
          {claim?.Received_Date?.toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
          })}
        </span>
      </div>

      {/* Expand toggle */}
      <button
        name="btnShowHideDetailsCard"
        onClick={onToggle}
        style={{
          marginTop: "auto",
          padding: "8px 12px",
          borderRadius: 8,
          border: "1px solid #E8ECF0",
          background: expanded ? "rgba(13,148,136,0.05)" : "#fff",
          color: "#0D9488",
          fontSize: 12,
          fontWeight: 700,
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 5,
          fontFamily: "inherit",
        }}
      >
        {/*{expanded ? <ChevronUp size={13} strokeWidth={2.5} /> : <ChevronDown size={13} strokeWidth={2.5} />}*/}
        {/*{expanded ? 'Hide details' : 'Show details'}*/}
        {loading ? (
          <>Loading...</>
        ) : expanded ? (
          <>
            <ChevronUp size={13} strokeWidth={2.5} /> Hide details
          </>
        ) : (
          <>
            <ChevronDown size={13} strokeWidth={2.5} /> Show details
          </>
        )}
        {/*{claimDetailsLoading ? 'Loading...' : expanded ? 'Hide details' : 'Show details'}*/}
      </button>

      {/* Expanded details */}
      {expanded && (
        <div
          style={{
            marginTop: 4,
            padding: "12px 14px",
            borderRadius: 10,
            background: "rgba(13,148,136,0.03)",
            borderLeft: "3px solid #0D9488",
            fontSize: 12,
            color: "#374151",
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          {claimDetails?.patientAccountNo && (
            <div>
              <strong
                style={{
                  color: "#6B7280",
                  fontSize: 10,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                Patient Account
              </strong>
              <div>{claimDetails?.patientAccountNo}</div>
            </div>
          )}
          {claimDetails?.checkNumber && (
            <div>
              <strong
                style={{
                  color: "#6B7280",
                  fontSize: 10,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                Check #
              </strong>
              <div>
                {claimDetails?.checkNumber}{" "}
                {claimDetails?.checkDate &&
                  `· ${claimDetails?.checkDate.replace(/-/g, "/")}`}
              </div>
            </div>
          )}
          {claimDetails?.claimNumber && (
            <div>
              <strong
                style={{
                  color: "#6B7280",
                  fontSize: 10,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                EOP Request
              </strong>
              <div>
                <Receipt
                  size={11}
                  style={{ display: "inline", marginRight: 4 }}
                />
                $
                {claimDetails?.paidAmount.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
            </div>
          )}
          {claimDetails?.exCode && (
            <div>
              <strong
                style={{
                  color: "#6B7280",
                  fontSize: 10,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                EX Code
              </strong>
              <div>{claimDetails?.exCode}</div>
            </div>
          )}
          {(claimDetails?.lineItems || []).length > 0 && (
            <Link
              /*to={`/claims/${claimDetails.claimNumber}`}*/
              /*to={`/claims/${claimDetails?.claimNumber}/${claimDetails?.checkNumber || 'null'}/${claimDetails?.billingProvider}`}*/
              to={`/claims/${claimDetails?.claimNumber}/${claimDetails?.checkNumber || "null"}/${billingProviderId}`}
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "#0D9488",
                textDecoration: "none",
                marginTop: 4,
              }}
              state={{
                results,
                totalRecords,
                dataState,
                viewMode,
                searchText,
                expandedItems,
                claimDetails,
                billingProviderId,
                groupId,
                searchBy,
                searchValue,
                searchFirstName,
                searchLastName,
                searchDob,
                serviceStart,
                serviceEnd,
                searched,
                selectedUserId,
                totalSummary,
              }}
            >
              View {claimDetails?.lineItems?.length} line item
              {claimDetails?.lineItems?.length !== 1 ? "s" : ""} →
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

const toggleBtnStyle = (active) => ({
  padding: "7px 12px",
  borderRadius: 8,
  border: "none",
  background: active ? "#fff" : "transparent",
  color: active ? "#0D9488" : "#6B7280",
  fontSize: 12,
  fontWeight: 700,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  boxShadow: active ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
  fontFamily: "inherit",
  transition: "all 0.15s",
});

export default ClaimStatusPage;
